import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kOrgBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { LeaveOrganizationResponseSchema, type LeaveOrganizationResponse } from '@/organizations/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

function getErrorDetail(data: unknown): unknown {
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as { detail?: unknown }).detail : undefined
}

/** Remove the authenticated user from their organization. */
export async function postLeaveOrganization(): Promise<LeaveOrganizationResponse> {
  const sentryTransactionId = nanoid()

  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')

    const token = await user.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kOrgBasePath}/leave`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
    })
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? undefined : data
      const detail = getErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Leave organization request failed - status: ${response.status}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Leave organization request failed - status: ${response.status}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Leave organization response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            parseError: formatUnknownError(parseError),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid leave organization response JSON')
    }

    const parsed = LeaveOrganizationResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Leave organization response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid leave organization response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
